import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { AuthService } from '@core/services/auth.service';
import { SessionIdleService } from '@core/services/session-idle.service';

/**
 * 4.7 (R29, DCMPDEV-738 / DCM-366): the idle warning.
 *
 * Mounted ONLY on signed-in parent pages (the dashboard shell and My
 * profile) — never on the login/account-setup/MFA screens. After 25 minutes
 * without API activity (the backend ends the session at 30) it asks
 * "Still there?" and offers to keep the session alive.
 *
 * "Stay signed in" pings the existing whoami endpoint: a successful answer
 * resets the server's idle timer, and the interceptor's touch() (fired by
 * that same successful response) resets the client clock. The clock is reset
 * here FIRST so the warning hides immediately. If the session is already
 * gone, whoami answers 401 and the interceptor routes to /login with the
 * expired notice — no dead-end UI.
 */
@Component({
  selector: 'app-session-idle-warning',
  imports: [MatButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (idle.warning()) {
      <div class="idle-warning" role="alert" data-testid="idle-warning">
        <span>Still there? Your session ends in 5 minutes</span>
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="staySignedIn()"
          data-testid="idle-stay-signed-in"
        >
          Stay signed in
        </button>
      </div>
    }
  `,
  styles: `
    .idle-warning {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 200;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 10px 16px;
      background: var(--gp-pending-bg, #fff8e1);
      color: #6b4d07;
      border-bottom: 1.5px solid #f3d5a8;
      font-size: 14px;
      font-weight: 600;
    }
  `,
})
export class SessionIdleWarningComponent {
  readonly idle = inject(SessionIdleService);
  private readonly auth = inject(AuthService);

  /** Keep the session alive: reset the clock, then ping whoami. */
  staySignedIn(): void {
    this.idle.touch();
    // A 401 here is handled by the interceptor (routes to /login with the
    // expired notice); a network failure just leaves the clock running —
    // the next successful response resets it. Nothing to surface inline.
    this.auth.whoami().subscribe({ error: () => undefined });
  }
}